import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { 
  CalendarIcon,
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  DollarSign, 
  CreditCard, 
  Shield, 
  Heart,
  Eye,
  LifeBuoy,
  PiggyBank,
  Calculator,
  Clock,
  TrendingUp,
  FileText,
  CheckCircle,
  AlertCircle,
  Save,
  X,
  Receipt,
  ArrowRight,
  ArrowLeft,
  ChevronRight
} from "lucide-react"
import { format } from "date-fns"
import { MoonLoader } from "@/components/ui/moon-loader"
import { TranslatedText } from "@/components/translated-text"
import { useTranslation } from "@/hooks/useTranslation"

interface EmployeeModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  employee?: any
}

export function EmployeeModal({ isOpen, onClose, onSubmit, employee }: EmployeeModalProps) {
  const { tSync } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 6
  const [formData, setFormData] = useState({
    firstName: employee?.firstName || '',
    lastName: employee?.lastName || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    address: employee?.address || '',
    city: employee?.city || '',
    state: employee?.state || '',
    zipCode: employee?.zipCode || '',
    dateOfBirth: employee?.dateOfBirth || '',
    socialSecurityNumber: employee?.socialSecurityNumber || '',
    nationalId: employee?.nationalId || '',
    rssbNumber: employee?.rssbNumber || '',
    bankName: employee?.bankName || '',
    bankAccountNumber: employee?.bankAccountNumber || '',
    hireDate: employee?.hireDate || '',
    department: employee?.department || '',
    position: employee?.position || '',
    employmentType: employee?.employmentType || 'full_time',
    payRate: employee?.payRate?.toString() || '',
    payRateType: employee?.payRateType || 'hourly',
    basicSalary: employee?.basicSalary?.toString() || '',
    salaryCurrency: employee?.salaryCurrency || 'RWF',
    salaryEffectiveDate: employee?.salaryEffectiveDate || '',
    payFrequency: employee?.payFrequency || 'biweekly',
    overtimeRate: employee?.overtimeRate?.toString() || '1.5',
    overtimeThreshold: employee?.overtimeThreshold?.toString() || '40',
    overtimeThresholdPeriod: employee?.overtimeThresholdPeriod || 'weekly', // 'daily' or 'weekly'
    taxFilingStatus: employee?.taxFilingStatus || 'single',
    federalExemptions: employee?.federalExemptions?.toString() || '0',
    stateExemptions: employee?.stateExemptions?.toString() || '0',
    additionalWithholding: employee?.additionalWithholding?.toString() || '0',
    healthInsurance: employee?.healthInsurance || false,
    healthInsuranceAmount: employee?.healthInsuranceAmount?.toString() || '',
    dentalInsurance: employee?.dentalInsurance || false,
    dentalInsuranceAmount: employee?.dentalInsuranceAmount?.toString() || '',
    visionInsurance: employee?.visionInsurance || false,
    visionInsuranceAmount: employee?.visionInsuranceAmount?.toString() || '',
    lifeInsurance: employee?.lifeInsurance || false,
    lifeInsuranceAmount: employee?.lifeInsuranceAmount?.toString() || '',
    retirement401k: employee?.retirement401k || false,
    retirement401kRate: employee?.retirement401kRate?.toString() || '',
    // Rwanda-specific deductions
    payeAmount: employee?.payeAmount?.toString() || '', // PAYE tax amount (calculated automatically, editable)
    pensionRate: employee?.pensionRate?.toString() || '6', // Social Security Pension rate (percentage)
    maternityRate: employee?.maternityRate?.toString() || '0.30', // Social Security Maternity rate (percentage)
    hazardRate: employee?.hazardRate?.toString() || '2', // Hazard deduction rate (percentage)
    cbhiRate: employee?.cbhiRate?.toString() || '0.50' // CBHI rate (percentage)
  })
  const [payeManuallyEdited, setPayeManuallyEdited] = useState(false)

  // Clear errors and reset step when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setErrors({})
      setCurrentStep(1)
      setPayeManuallyEdited(false)
    } else {
      setCurrentStep(1)
      setPayeManuallyEdited(false)
    }
  }, [isOpen])

  // Update form data when employee prop changes (for edit mode)
  useEffect(() => {
    if (employee) {
      setErrors({}) // Clear errors when loading employee data
      setFormData({
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.email || '',
        phone: employee.phone || '',
        address: employee.address || '',
        city: employee.city || '',
        state: employee.state || '',
        zipCode: employee.zipCode || '',
        dateOfBirth: employee.dateOfBirth || '',
        socialSecurityNumber: employee.socialSecurityNumber || '',
        nationalId: employee.nationalId || '',
        rssbNumber: employee.rssbNumber || '',
        bankName: employee.bankName || '',
        bankAccountNumber: employee.bankAccountNumber || '',
        hireDate: employee.hireDate || '',
        department: employee.department || '',
        position: employee.position || '',
        employmentType: employee.employmentType || 'full_time',
        payRate: employee.payRate?.toString() || '',
        payRateType: employee.payRateType || 'hourly',
        basicSalary: employee.basicSalary?.toString() || '',
        salaryCurrency: employee.salaryCurrency || 'RWF',
        salaryEffectiveDate: employee.salaryEffectiveDate || '',
        payFrequency: employee.payFrequency || 'biweekly',
        overtimeRate: employee.overtimeRate?.toString() || '1.5',
        overtimeThreshold: employee.overtimeThreshold?.toString() || '40',
        overtimeThresholdPeriod: employee.overtimeThresholdPeriod || 'weekly',
        taxFilingStatus: employee.taxFilingStatus || 'single',
        federalExemptions: employee.federalExemptions?.toString() || '0',
        stateExemptions: employee.stateExemptions?.toString() || '0',
        additionalWithholding: employee.additionalWithholding?.toString() || '0',
        healthInsurance: employee.healthInsurance || false,
        healthInsuranceAmount: employee.healthInsuranceAmount?.toString() || '',
        dentalInsurance: employee.dentalInsurance || false,
        dentalInsuranceAmount: employee.dentalInsuranceAmount?.toString() || '',
        visionInsurance: employee.visionInsurance || false,
        visionInsuranceAmount: employee.visionInsuranceAmount?.toString() || '',
        lifeInsurance: employee.lifeInsurance || false,
        lifeInsuranceAmount: employee.lifeInsuranceAmount?.toString() || '',
        retirement401k: employee.retirement401k || false,
        retirement401kRate: employee.retirement401kRate?.toString() || '',
        // Rwanda-specific deductions
        payeAmount: employee.payeAmount?.toString() || '',
        pensionRate: employee.pensionRate?.toString() || '6',
        maternityRate: employee.maternityRate?.toString() || '0.30',
        hazardRate: employee.hazardRate?.toString() || '2',
        cbhiRate: employee.cbhiRate?.toString() || '0.50'
      })
      setPayeManuallyEdited(false)
    }
  }, [employee])

  // Calculate Rwanda PAYE based on progressive tax brackets
  const calculateRwandaPAYE = (monthlyGrossPay: number): number => {
    if (monthlyGrossPay <= 0) return 0;
    
    let tax = 0;
    
    // Progressive tax calculation based on Rwanda tax brackets
    if (monthlyGrossPay <= 60000) {
      // 0 - 60,000: 0%
      tax = 0;
    } else if (monthlyGrossPay <= 100000) {
      // 60,001 - 100,000: 10% on amount above 60,000
      tax = (monthlyGrossPay - 60000) * 0.10;
    } else if (monthlyGrossPay <= 200000) {
      // 100,001 - 200,000: 10% on 40,000 + 20% on amount above 100,000
      tax = (100000 - 60000) * 0.10 + (monthlyGrossPay - 100000) * 0.20;
    } else {
      // Above 200,000: 10% on 40,000 + 20% on 100,000 + 30% on amount above 200,000
      tax = (100000 - 60000) * 0.10 + (200000 - 100000) * 0.20 + (monthlyGrossPay - 200000) * 0.30;
    }
    
    return Math.round(tax * 100) / 100;
  }

  // Get detailed PAYE calculation breakdown
  const getPAYECalculationBreakdown = () => {
    const monthlyGross = getMonthlyGrossPay();
    if (monthlyGross <= 0) return null;

    const breakdown = {
      monthlyGross,
      brackets: [] as Array<{ range: string; taxable: number; rate: number; tax: number }>,
      totalTax: 0
    };

    if (monthlyGross <= 60000) {
      breakdown.brackets.push({
        range: '0 - 60,000',
        taxable: monthlyGross,
        rate: 0,
        tax: 0
      });
    } else if (monthlyGross <= 100000) {
      breakdown.brackets.push({
        range: '0 - 60,000',
        taxable: 60000,
        rate: 0,
        tax: 0
      });
      const taxableInBracket = monthlyGross - 60000;
      const taxInBracket = taxableInBracket * 0.10;
      breakdown.brackets.push({
        range: '60,001 - 100,000',
        taxable: taxableInBracket,
        rate: 10,
        tax: taxInBracket
      });
    } else if (monthlyGross <= 200000) {
      breakdown.brackets.push({
        range: '0 - 60,000',
        taxable: 60000,
        rate: 0,
        tax: 0
      });
      breakdown.brackets.push({
        range: '60,001 - 100,000',
        taxable: 40000,
        rate: 10,
        tax: 4000
      });
      const taxableInBracket = monthlyGross - 100000;
      const taxInBracket = taxableInBracket * 0.20;
      breakdown.brackets.push({
        range: '100,001 - 200,000',
        taxable: taxableInBracket,
        rate: 20,
        tax: taxInBracket
      });
    } else {
      breakdown.brackets.push({
        range: '0 - 60,000',
        taxable: 60000,
        rate: 0,
        tax: 0
      });
      breakdown.brackets.push({
        range: '60,001 - 100,000',
        taxable: 40000,
        rate: 10,
        tax: 4000
      });
      breakdown.brackets.push({
        range: '100,001 - 200,000',
        taxable: 100000,
        rate: 20,
        tax: 20000
      });
      const taxableInBracket = monthlyGross - 200000;
      const taxInBracket = taxableInBracket * 0.30;
      breakdown.brackets.push({
        range: 'Above 200,000',
        taxable: taxableInBracket,
        rate: 30,
        tax: taxInBracket
      });
    }

    breakdown.totalTax = breakdown.brackets.reduce((sum, bracket) => sum + bracket.tax, 0);
    return breakdown;
  }

  // Calculate monthly gross pay from form data
  const getMonthlyGrossPay = (): number => {
    if (formData.payRateType === 'salary' && formData.basicSalary) {
      // For salary, basicSalary is already monthly
      return Number(formData.basicSalary) || 0;
    } else if (formData.payRateType === 'hourly' && formData.payRate) {
      // For hourly, estimate monthly: hourly rate * 8 hours * 22 working days
      const hourlyRate = Number(formData.payRate) || 0;
      return hourlyRate * 8 * 22; // Approximate monthly gross
    }
    return 0;
  }

  // Recalculate PAYE when salary changes (unless manually edited)
  useEffect(() => {
    if (!payeManuallyEdited && (formData.basicSalary || formData.payRate)) {
      const monthlyGross = getMonthlyGrossPay();
      if (monthlyGross > 0) {
        const calculatedPAYE = calculateRwandaPAYE(monthlyGross);
        setFormData(prev => ({
          ...prev,
          payeAmount: calculatedPAYE > 0 ? calculatedPAYE.toFixed(2) : ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          payeAmount: ''
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.basicSalary, formData.payRate, formData.payRateType, payeManuallyEdited])

  // Simple and Reliable Date Picker Component
  const ModernDatePicker = ({ 
    label, 
    value, 
    onChange, 
    placeholder = "Select date",
    required = false,
    error
  }: {
    label: string
    value: string
    onChange: (date: string) => void
    placeholder?: string
    required?: boolean
    error?: string
  }) => {
    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedDate = e.target.value
      // Convert date to ISO-8601 DateTime format for Prisma, or null for empty dates
      if (selectedDate) {
        const isoDateTime = new Date(selectedDate + 'T00:00:00.000Z').toISOString()
        onChange(isoDateTime)
      } else {
        onChange(null) // Send null instead of empty string for Prisma
      }
    }

    return (
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
          <CalendarIcon className="w-4 h-4" />
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
        <Input
          type="date"
          value={value && value !== null ? value.split('T')[0] : ''}
          onChange={handleDateChange}
          className={`h-11 border ${error ? 'border-red-500' : 'border-slate-300'}`}
          required={required}
        />
        {error && (
          <p className="text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}
      </div>
    )
  }

  // Step validation functions
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}
    
    switch (step) {
      case 1: // Personal Information
        if (!formData.firstName || formData.firstName.trim() === '') {
          newErrors.firstName = tSync('First name is required')
        }
        if (!formData.lastName || formData.lastName.trim() === '') {
          newErrors.lastName = tSync('Last name is required')
        }
        if (!formData.email || formData.email.trim() === '') {
          newErrors.email = tSync('Email is required')
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = tSync('Please enter a valid email address')
        }
        break
      case 2: // Banking Information (optional, no validation needed)
        break
      case 3: // Employment Information
        if (!formData.hireDate || formData.hireDate.trim() === '') {
          newErrors.hireDate = tSync('Hire date is required')
        }
        break
      case 4: // Pay Information
        if (!formData.payRateType || formData.payRateType.trim() === '') {
          newErrors.payRateType = tSync('Pay rate type is required')
        }
        if (!formData.payFrequency || formData.payFrequency.trim() === '') {
          newErrors.payFrequency = tSync('Pay frequency is required')
        }
        if (formData.payRateType === 'hourly' && (!formData.payRate || formData.payRate.trim() === '')) {
          newErrors.payRate = tSync('Hourly rate is required')
        }
        if (formData.payRateType === 'salary' && (!formData.basicSalary || formData.basicSalary.trim() === '')) {
          newErrors.basicSalary = tSync('Monthly basic salary is required')
        }
        break
      case 5: // Deductions (optional, no validation needed)
        break
      case 6: // Benefits (optional, no validation needed)
        break
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return false
    }
    
    setErrors({})
    return true
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1)
      }
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      setErrors({}) // Clear errors when going back
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate all steps before submission
    for (let step = 1; step <= totalSteps; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step)
        return
      }
    }
    
    setIsLoading(true)
    try {
      // Convert string values to proper types for Prisma
      // Exclude payeAmount as it's only for preview - backend calculates PAYE automatically
      const { payeAmount, ...formDataWithoutPaye } = formData;
      const processedData = {
        ...formDataWithoutPaye,
        federalExemptions: formData.federalExemptions ? parseInt(formData.federalExemptions) : 0,
        stateExemptions: formData.stateExemptions ? parseInt(formData.stateExemptions) : 0,
        additionalWithholding: formData.additionalWithholding ? parseFloat(formData.additionalWithholding) : 0,
        payRate: formData.payRate ? parseFloat(formData.payRate) : 0,
        overtimeRate: formData.overtimeRate ? parseFloat(formData.overtimeRate) : 1.5,
        overtimeThreshold: formData.overtimeThreshold ? parseFloat(formData.overtimeThreshold) : 40,
        basicSalary: formData.basicSalary && formData.basicSalary.trim() !== '' ? parseFloat(formData.basicSalary) : null,
        retirement401kRate: formData.retirement401kRate ? parseFloat(formData.retirement401kRate) : null,
        healthInsuranceAmount: formData.healthInsuranceAmount ? parseFloat(formData.healthInsuranceAmount) : null,
        dentalInsuranceAmount: formData.dentalInsuranceAmount ? parseFloat(formData.dentalInsuranceAmount) : null,
        visionInsuranceAmount: formData.visionInsuranceAmount ? parseFloat(formData.visionInsuranceAmount) : null,
        lifeInsuranceAmount: formData.lifeInsuranceAmount ? parseFloat(formData.lifeInsuranceAmount) : null,
        // Rwanda-specific deductions
        // Note: PAYE is calculated automatically by backend during payroll processing
        // payeAmount is only shown in the form for preview/editing purposes
        pensionRate: formData.pensionRate ? parseFloat(formData.pensionRate) : 6,
        maternityRate: formData.maternityRate ? parseFloat(formData.maternityRate) : 0.30,
        hazardRate: formData.hazardRate ? parseFloat(formData.hazardRate) : 2,
        cbhiRate: formData.cbhiRate ? parseFloat(formData.cbhiRate) : 0.50,
        // Convert empty date strings to null for Prisma (hireDate is required)
        salaryEffectiveDate: formData.salaryEffectiveDate && formData.salaryEffectiveDate.trim() !== '' ? formData.salaryEffectiveDate : null,
        dateOfBirth: formData.dateOfBirth && formData.dateOfBirth.trim() !== '' ? formData.dateOfBirth : null,
        hireDate: formData.hireDate // Required field, already validated above
      }
      
      await onSubmit(processedData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getPayPeriodAmount = () => {
    if (!formData.basicSalary || !formData.payFrequency) return 0
    const periodsPerYear = formData.payFrequency === 'daily' ? 365 :
                           formData.payFrequency === 'weekly' ? 52 :
                           formData.payFrequency === 'biweekly' ? 26 :
                           formData.payFrequency === 'semimonthly' ? 24 : 12
    return Number(formData.basicSalary) / periodsPerYear
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <User className="w-6 h-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-2xl font-semibold text-slate-900">
                {employee ? <TranslatedText text="Edit Employee" /> : <TranslatedText text="Add New Employee" />}
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-1">
                {employee ? <TranslatedText text="Update employee information and payroll settings" /> : <TranslatedText text="Add a new employee to your organization with complete details" />}
              </DialogDescription>
            </div>
          </div>
          
          {/* Progress Indicator */}
          <div className="mt-4">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                        currentStep >= step
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-teal-100 text-teal-600'
                      }`}
                    >
                      {currentStep > step ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        step
                      )}
                    </div>
                    <span className="text-xs mt-1 text-slate-600 hidden sm:block">
                      {step === 1 && <TranslatedText text="Personal" />}
                      {step === 2 && <TranslatedText text="Banking" />}
                      {step === 3 && <TranslatedText text="Employment" />}
                      {step === 4 && <TranslatedText text="Pay" />}
                      {step === 5 && <TranslatedText text="Deductions" />}
                      {step === 6 && <TranslatedText text="Benefits" />}
                    </span>
                  </div>
                  {step < totalSteps && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${
                        currentStep > step ? 'bg-primary' : 'bg-teal-100'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-2">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900"><TranslatedText text="Personal Information" /></CardTitle>
                </div>
                <CardDescription><TranslatedText text="Basic personal details and contact information" /></CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <TranslatedText text="First Name" /> <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => {
                        setFormData({ ...formData, firstName: e.target.value })
                        if (errors.firstName) {
                          setErrors({ ...errors, firstName: '' })
                        }
                      }}
                      className={`h-11 border ${errors.firstName ? 'border-red-500' : 'border-slate-300'}`}
                      placeholder={tSync("Enter first name")}
                      required
                    />
                    {errors.firstName && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <TranslatedText text="Last Name" /> <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => {
                        setFormData({ ...formData, lastName: e.target.value })
                        if (errors.lastName) {
                          setErrors({ ...errors, lastName: '' })
                        }
                      }}
                      className={`h-11 border ${errors.lastName ? 'border-red-500' : 'border-slate-300'}`}
                      placeholder={tSync("Enter last name")}
                      required
                    />
                    {errors.lastName && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      <TranslatedText text="Email Address" /> <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value })
                        if (errors.email) {
                          setErrors({ ...errors, email: '' })
                        }
                      }}
                      className={`h-11 border ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
                      placeholder={tSync("employee@company.com")}
                      required
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      <TranslatedText text="Phone Number" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({ ...formData, phone: e.target.value })
                        if (errors.phone) {
                          setErrors({ ...errors, phone: '' })
                        }
                      }}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("+1 (555) 123-4567")}
                    />
                  </div>
                </div>
                
                <Separator className="my-4" />
                
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <TranslatedText text="Address Information" />
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2 space-y-2">
                      <Label htmlFor="address" className="text-sm font-medium text-slate-600"><TranslatedText text="Street Address" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span></Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="h-10 border border-slate-300"
                        placeholder={tSync("123 Main Street")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-sm font-medium text-slate-600"><TranslatedText text="City" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span></Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="h-10 border border-slate-300"
                        placeholder={tSync("New York")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state" className="text-sm font-medium text-slate-600"><TranslatedText text="State" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span></Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="h-10 border border-slate-300"
                        placeholder={tSync("NY")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zipCode" className="text-sm font-medium text-slate-600"><TranslatedText text="ZIP Code" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span></Label>
                      <Input
                        id="zipCode"
                        value={formData.zipCode}
                        onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                        className="h-10 border border-slate-300"
                        placeholder={tSync("10001")}
                      />
                    </div>
                  </div>
                </div>
                
                <Separator className="my-4" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ModernDatePicker
                    label={tSync("Date of Birth (Optional)")}
                    value={formData.dateOfBirth}
                    onChange={(date) => {
                      setFormData({ ...formData, dateOfBirth: date })
                    }}
                    placeholder={tSync("Select date of birth")}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="nationalId" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      <TranslatedText text="National ID (Rwanda)" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="nationalId"
                      value={formData.nationalId}
                      onChange={(e) => setFormData({ ...formData, nationalId: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("1 199880064187054")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rssbNumber" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      <TranslatedText text="RSSB Number" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="rssbNumber"
                      value={formData.rssbNumber}
                      onChange={(e) => setFormData({ ...formData, rssbNumber: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("111111111R")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="socialSecurityNumber" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      <TranslatedText text="Social Security Number (Optional)" />
                    </Label>
                    <Input
                      id="socialSecurityNumber"
                      value={formData.socialSecurityNumber}
                      onChange={(e) => setFormData({ ...formData, socialSecurityNumber: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("XXX-XX-XXXX")}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Step 2: Banking Information */}
            {currentStep === 2 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900"><TranslatedText text="Banking Information" /></CardTitle>
                </div>
                <CardDescription><TranslatedText text="Bank account details for payroll" /></CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="bankName" className="text-sm font-medium text-slate-600"><TranslatedText text="Bank Name" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span></Label>
                    <Select
                      value={formData.bankName}
                      onValueChange={(value) => setFormData({ ...formData, bankName: value })}
                    >
                      <SelectTrigger id="bankName" className="h-11 w-full border border-slate-300">
                        <SelectValue placeholder={tSync("Select a bank")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BK (Bank of Kigali)">BK (<TranslatedText text="Bank of Kigali" />)</SelectItem>
                        <SelectItem value="Equity Bank Rwanda"><TranslatedText text="Equity Bank Rwanda" /></SelectItem>
                        <SelectItem value="Ecobank Rwanda"><TranslatedText text="Ecobank Rwanda" /></SelectItem>
                        <SelectItem value="I&M Bank Rwanda">I&M <TranslatedText text="Bank Rwanda" /></SelectItem>
                        <SelectItem value="BPR Bank Rwanda">BPR <TranslatedText text="Bank Rwanda" /></SelectItem>
                        <SelectItem value="Access Bank Rwanda"><TranslatedText text="Access Bank Rwanda" /></SelectItem>
                        <SelectItem value="Bank of Africa Rwanda"><TranslatedText text="Bank of Africa Rwanda" /></SelectItem>
                        <SelectItem value="GTBank Rwanda">GTBank <TranslatedText text="Rwanda" /></SelectItem>
                        <SelectItem value="Cogebanque"><TranslatedText text="Cogebanque" /></SelectItem>
                        <SelectItem value="Urwego Opportunity Bank"><TranslatedText text="Urwego Opportunity Bank" /></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bankAccountNumber" className="text-sm font-medium text-slate-600"><TranslatedText text="Bank Account Number" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span></Label>
                    <Input
                      id="bankAccountNumber"
                      value={formData.bankAccountNumber}
                      onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("12321233")}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Step 3: Employment Information */}
            {currentStep === 3 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-green-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900"><TranslatedText text="Employment Information" /></CardTitle>
                </div>
                <CardDescription><TranslatedText text="Job details and employment status" /></CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ModernDatePicker
                    label={tSync("Hire Date")}
                    value={formData.hireDate}
                    onChange={(date) => {
                      setFormData({ ...formData, hireDate: date })
                      if (errors.hireDate) {
                        setErrors({ ...errors, hireDate: '' })
                      }
                    }}
                    placeholder={tSync("Select hire date")}
                    required={true}
                    error={errors.hireDate}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="employmentType" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      <TranslatedText text="Employment Type" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Select
                      value={formData.employmentType}
                      onValueChange={(value) => setFormData({ ...formData, employmentType: value })}
                    >
                      <SelectTrigger className="h-11 w-full border border-slate-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <TranslatedText text="Full Time" />
                          </div>
                        </SelectItem>
                        <SelectItem value="part_time">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <TranslatedText text="Part Time" />
                          </div>
                        </SelectItem>
                        <SelectItem value="contractor">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                            <TranslatedText text="Contractor" />
                          </div>
                        </SelectItem>
                        <SelectItem value="intern">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <TranslatedText text="Intern" />
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Building className="w-4 h-4" />
                      <TranslatedText text="Department" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("Engineering, Sales, Marketing...")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="position" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      <TranslatedText text="Position/Title" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="position"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("Software Engineer, Sales Manager...")}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Step 4: Pay Information - I'll need to find this section and wrap it */}
            {currentStep === 4 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900"><TranslatedText text="Pay Information" /></CardTitle>
                </div>
                <CardDescription><TranslatedText text="Compensation details and pay structure" /></CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="payRateType" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <CreditCard className="w-4 h-4" />
                      <TranslatedText text="Pay Rate Type" /> <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.payRateType}
                      onValueChange={(value) => {
                        setFormData({ ...formData, payRateType: value })
                        if (errors.payRateType) {
                          setErrors({ ...errors, payRateType: '' })
                        }
                      }}
                    >
                      <SelectTrigger className={`h-11 w-full border ${errors.payRateType ? 'border-red-500' : 'border-slate-300'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <TranslatedText text="Hourly" />
                          </div>
                        </SelectItem>
                        <SelectItem value="salary">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <TranslatedText text="Salary" />
                          </div>
                        </SelectItem>
                        <SelectItem value="commission">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-purple-600" />
                            <TranslatedText text="Commission" />
                          </div>
                        </SelectItem>
                        <SelectItem value="piece_rate">
                          <div className="flex items-center gap-2">
                            <Calculator className="w-4 h-4 text-orange-600" />
                            <TranslatedText text="Piece Rate" />
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.payRateType && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.payRateType}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payFrequency" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4" />
                      <TranslatedText text="Pay Frequency" /> <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.payFrequency}
                      onValueChange={(value) => {
                        setFormData({ ...formData, payFrequency: value })
                        if (errors.payFrequency) {
                          setErrors({ ...errors, payFrequency: '' })
                        }
                      }}
                    >
                      <SelectTrigger className={`h-11 w-full border ${errors.payFrequency ? 'border-red-500' : 'border-slate-300'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                            <TranslatedText text="Daily" />
                          </div>
                        </SelectItem>
                        <SelectItem value="weekly">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <TranslatedText text="Weekly" />
                          </div>
                        </SelectItem>
                        <SelectItem value="biweekly">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <TranslatedText text="Biweekly" />
                          </div>
                        </SelectItem>
                        <SelectItem value="semimonthly">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <TranslatedText text="Semi-monthly" />
                          </div>
                        </SelectItem>
                        <SelectItem value="monthly">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                            <TranslatedText text="Monthly" />
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.payFrequency && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.payFrequency}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Conditional fields based on pay rate type */}
                {formData.payRateType === 'hourly' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="payRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <TranslatedText text="Hourly Rate" /> ({formData.salaryCurrency || 'RWF'}) <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="payRate"
                          type="number"
                          step="0.01"
                          value={formData.payRate}
                          onChange={(e) => {
                            setFormData({ ...formData, payRate: e.target.value })
                            if (errors.payRate) {
                              setErrors({ ...errors, payRate: '' })
                            }
                          }}
                          className={`h-11 border ${errors.payRate ? 'border-red-500' : 'border-slate-300'}`}
                          placeholder={tSync("25.00")}
                          required
                        />
                        {errors.payRate && (
                          <p className="text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.payRate}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="overtimeRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          <Calculator className="w-4 h-4" />
                          Overtime Rate Multiplier <span className="text-slate-400 font-normal">(Optional)</span>
                        </Label>
                        <Input
                          id="overtimeRate"
                          type="number"
                          step="0.1"
                          value={formData.overtimeRate}
                          onChange={(e) => setFormData({ ...formData, overtimeRate: e.target.value })}
                          className="h-11 border border-slate-300"
                          placeholder="1.5"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {formData.payRateType === 'salary' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="basicSalary" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          <TranslatedText text="Monthly Basic Salary" /> ({formData.salaryCurrency || 'RWF'}) <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="basicSalary"
                          type="number"
                          step="0.01"
                          value={formData.basicSalary}
                          onChange={(e) => {
                            setFormData({ ...formData, basicSalary: e.target.value })
                            if (errors.basicSalary) {
                              setErrors({ ...errors, basicSalary: '' })
                            }
                          }}
                          className={`h-11 border ${errors.basicSalary ? 'border-red-500' : 'border-slate-300'}`}
                          placeholder={tSync("5000.00")}
                          required
                        />
                        {errors.basicSalary && (
                          <p className="text-sm text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.basicSalary}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="salaryCurrency" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          <CreditCard className="w-4 h-4" />
                          <TranslatedText text="Salary Currency" />
                        </Label>
                        <Select
                          value={formData.salaryCurrency}
                          onValueChange={(value) => setFormData({ ...formData, salaryCurrency: value })}
                        >
                          <SelectTrigger className="h-11 w-full border border-slate-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="RWF">RWF - Rwandan Franc</SelectItem>
                            <SelectItem value="USD">USD - US Dollar</SelectItem>
                            <SelectItem value="EUR">EUR - Euro</SelectItem>
                            <SelectItem value="GBP">GBP - British Pound</SelectItem>
                            <SelectItem value="KES">KES - Kenyan Shilling</SelectItem>
                            <SelectItem value="NGN">NGN - Nigerian Naira</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <ModernDatePicker
                      label={tSync("Salary Effective Date (Optional)")}
                      value={formData.salaryEffectiveDate}
                      onChange={(date) => setFormData({ ...formData, salaryEffectiveDate: date })}
                      placeholder={tSync("Select effective date")}
                    />
                  </div>
                )}
                
                {formData.payRateType === 'commission' && (
                  <div className="space-y-2">
                    <Label htmlFor="payRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      <TranslatedText text="Base Salary" /> ({formData.salaryCurrency || 'RWF'})
                    </Label>
                    <Input
                      id="payRate"
                      type="number"
                      step="0.01"
                      value={formData.payRate}
                      onChange={(e) => setFormData({ ...formData, payRate: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("Base salary for commission employees")}
                    />
                  </div>
                )}
                
                {formData.payRateType === 'piece_rate' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="payRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          <Calculator className="w-4 h-4" />
                          <TranslatedText text="Piece Rate" /> ({formData.salaryCurrency || 'RWF'})
                        </Label>
                        <Input
                          id="payRate"
                          type="number"
                          step="0.01"
                          value={formData.payRate}
                          onChange={(e) => setFormData({ ...formData, payRate: e.target.value })}
                          className="h-11 border border-slate-300"
                          placeholder={tSync("Rate per piece/unit")}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="overtimeRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          <Calculator className="w-4 h-4" />
                          Overtime Rate Multiplier <span className="text-slate-400 font-normal">(Optional)</span>
                        </Label>
                        <Input
                          id="overtimeRate"
                          type="number"
                          step="0.1"
                          value={formData.overtimeRate}
                          onChange={(e) => setFormData({ ...formData, overtimeRate: e.target.value })}
                          className="h-11 border border-slate-300"
                          placeholder="1.5"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Overtime Settings - Only for Hourly and Piece Rate employees */}
                {(formData.payRateType === 'hourly' || formData.payRateType === 'piece_rate') && (
                  <div className="space-y-4">
                    <div className="border-t border-slate-200 pt-4">
                      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-4">
                        <Clock className="w-4 h-4" />
                        <TranslatedText text="Overtime Settings" />
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="overtimeThreshold" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <TranslatedText text="Overtime Threshold (hours)" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                          </Label>
                          <Input
                            id="overtimeThreshold"
                            type="number"
                            value={formData.overtimeThreshold}
                            onChange={(e) => setFormData({ ...formData, overtimeThreshold: e.target.value })}
                            className="h-11 border border-slate-300"
                            placeholder={formData.overtimeThresholdPeriod === 'daily' ? '8' : '40'}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="overtimeThresholdPeriod" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <TranslatedText text="Overtime Period" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                          </Label>
                          <Select
                            value={formData.overtimeThresholdPeriod}
                            onValueChange={(value) => setFormData({ ...formData, overtimeThresholdPeriod: value })}
                          >
                            <SelectTrigger className="h-11 w-full border border-slate-300">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                                  <TranslatedText text="Daily (per day)" />
                                </div>
                              </SelectItem>
                              <SelectItem value="weekly">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <TranslatedText text="Weekly (per week)" />
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Step 5: Deductions */}
            {currentStep === 5 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900"><TranslatedText text="Deductions" /></CardTitle>
                </div>
                <CardDescription><TranslatedText text="Social security and tax deduction rates for Rwanda payroll" /></CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="payeAmount" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      <TranslatedText text="PAYE Tax Amount" /> ({formData.salaryCurrency || 'RWF'})
                    </Label>
                    <Input
                      id="payeAmount"
                      type="number"
                      step="0.01"
                      value={formData.payeAmount}
                      onChange={(e) => {
                        setFormData({ ...formData, payeAmount: e.target.value })
                        setPayeManuallyEdited(true)
                        if (errors.payeAmount) {
                          setErrors({ ...errors, payeAmount: '' })
                        }
                      }}
                      className={`h-11 border ${errors.payeAmount ? 'border-red-500' : 'border-slate-300'}`}
                      placeholder={tSync("0.00")}
                    />
                    <p className="text-xs text-slate-500">
                      <TranslatedText text="Automatically calculated based on salary and Rwanda tax brackets. You can edit this amount if needed." />
                    </p>
                    {(() => {
                      const breakdown = getPAYECalculationBreakdown();
                      if (!breakdown || breakdown.monthlyGross <= 0) return null;
                      
                      return (
                        <div className="bg-green-50 border border-green-300 rounded-md p-4 space-y-3">
                          <p className="text-xs font-semibold text-green-900 mb-2">
                            <TranslatedText text="PAYE Calculation Breakdown:" />
                          </p>
                          <div className="space-y-2">
                            <div className="text-xs text-green-800">
                              <span className="font-semibold"><TranslatedText text="Monthly Gross Salary:" /></span>{' '}
                              {breakdown.monthlyGross.toLocaleString()} {formData.salaryCurrency || 'RWF'}
                            </div>
                            <div className="border-t border-green-200 pt-2 space-y-1.5">
                              {breakdown.brackets.map((bracket, idx) => (
                                <div key={idx} className="text-xs text-green-800">
                                  <div className="flex items-start gap-2">
                                    <span className="font-medium min-w-[140px]">{bracket.range} RWF:</span>
                                    <span className="text-green-700 flex-1">
                                      {bracket.taxable.toLocaleString()} × {bracket.rate}% = <span className="font-semibold">{bracket.tax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> RWF
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="border-t-2 border-green-400 pt-2 flex justify-between items-center">
                              <span className="text-xs font-bold text-green-900">
                                <TranslatedText text="Total PAYE Tax:" />
                              </span>
                              <span className="text-sm font-bold text-green-900">
                                {breakdown.totalTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {formData.salaryCurrency || 'RWF'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pensionRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      <TranslatedText text="Social Security Pension Rate (%)" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="pensionRate"
                      type="number"
                      step="0.01"
                      value={formData.pensionRate}
                      onChange={(e) => setFormData({ ...formData, pensionRate: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("6.00")}
                    />
                    <p className="text-xs text-slate-500"><TranslatedText text="Employee contribution: 6% (default)" /></p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maternityRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      <TranslatedText text="Social Security Maternity Rate (%)" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="maternityRate"
                      type="number"
                      step="0.01"
                      value={formData.maternityRate}
                      onChange={(e) => setFormData({ ...formData, maternityRate: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("0.30")}
                    />
                    <p className="text-xs text-slate-500"><TranslatedText text="Employee contribution: 0.30% (default)" /></p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cbhiRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      <TranslatedText text="CBHI Rate (%)" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="cbhiRate"
                      type="number"
                      step="0.01"
                      value={formData.cbhiRate}
                      onChange={(e) => setFormData({ ...formData, cbhiRate: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("0.50")}
                    />
                    <p className="text-xs text-slate-500"><TranslatedText text="Community-Based Health Insurance: 0.50% (default)" /></p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hazardRate" className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      <TranslatedText text="Hazard Rate (%)" /> <span className="text-slate-400 font-normal">(<TranslatedText text="Optional" />)</span>
                    </Label>
                    <Input
                      id="hazardRate"
                      type="number"
                      step="0.01"
                      value={formData.hazardRate}
                      onChange={(e) => setFormData({ ...formData, hazardRate: e.target.value })}
                      className="h-11 border border-slate-300"
                      placeholder={tSync("2.00")}
                    />
                    <p className="text-xs text-slate-500"><TranslatedText text="Hazard deduction: 2% (default)" /></p>
                  </div>
                </div>
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-sm text-cyan-800">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span className="font-medium"><TranslatedText text="Note:" /></span>
                  </div>
                  <p className="mt-1">
                    <TranslatedText text="These rates are used to calculate deductions from gross salary. PAYE is calculated based on tax brackets, while Social Security rates are fixed percentages of gross salary." />
                  </p>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Step 6: Benefits */}
            {currentStep === 6 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900"><TranslatedText text="Benefits Information" /></CardTitle>
                </div>
                <CardDescription><TranslatedText text="Employee benefits and insurance coverage" /></CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Heart className="w-4 h-4" />
                      <TranslatedText text="Insurance Benefits" />
                    </h4>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="healthInsurance"
                            checked={formData.healthInsurance}
                            onChange={(e) => setFormData({ ...formData, healthInsurance: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Label htmlFor="healthInsurance" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <Heart className="w-4 h-4 text-red-500" />
                            <TranslatedText text="Health Insurance" />
                          </Label>
                        </div>
                        {formData.healthInsurance && (
                          <div className="ml-7 space-y-2">
                            <Label htmlFor="healthInsuranceAmount" className="text-sm font-medium text-slate-600">
                              <TranslatedText text="Amount (Frw)" />
                            </Label>
                            <Input
                              id="healthInsuranceAmount"
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.healthInsuranceAmount}
                              onChange={(e) => setFormData({ ...formData, healthInsuranceAmount: e.target.value })}
                              placeholder={tSync("Enter amount")}
                              className="h-11 w-full border border-slate-300"
                            />
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="dentalInsurance"
                            checked={formData.dentalInsurance}
                            onChange={(e) => setFormData({ ...formData, dentalInsurance: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Label htmlFor="dentalInsurance" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-blue-500" />
                            <TranslatedText text="Dental Insurance" />
                          </Label>
                        </div>
                        {formData.dentalInsurance && (
                          <div className="ml-7 space-y-2">
                            <Label htmlFor="dentalInsuranceAmount" className="text-sm font-medium text-slate-600">
                              <TranslatedText text="Amount (Frw)" />
                            </Label>
                            <Input
                              id="dentalInsuranceAmount"
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.dentalInsuranceAmount}
                              onChange={(e) => setFormData({ ...formData, dentalInsuranceAmount: e.target.value })}
                              placeholder={tSync("Enter amount")}
                              className="h-11 w-full border border-slate-300"
                            />
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="visionInsurance"
                            checked={formData.visionInsurance}
                            onChange={(e) => setFormData({ ...formData, visionInsurance: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Label htmlFor="visionInsurance" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <Eye className="w-4 h-4 text-purple-500" />
                            <TranslatedText text="Vision Insurance" />
                          </Label>
                        </div>
                        {formData.visionInsurance && (
                          <div className="ml-7 space-y-2">
                            <Label htmlFor="visionInsuranceAmount" className="text-sm font-medium text-slate-600">
                              <TranslatedText text="Amount (Frw)" />
                            </Label>
                            <Input
                              id="visionInsuranceAmount"
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.visionInsuranceAmount}
                              onChange={(e) => setFormData({ ...formData, visionInsuranceAmount: e.target.value })}
                              placeholder={tSync("Enter amount")}
                              className="h-11 w-full border border-slate-300"
                            />
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="lifeInsurance"
                            checked={formData.lifeInsurance}
                            onChange={(e) => setFormData({ ...formData, lifeInsurance: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Label htmlFor="lifeInsurance" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <LifeBuoy className="w-4 h-4 text-green-500" />
                            <TranslatedText text="Life Insurance" />
                          </Label>
                        </div>
                        {formData.lifeInsurance && (
                          <div className="ml-7 space-y-2">
                            <Label htmlFor="lifeInsuranceAmount" className="text-sm font-medium text-slate-600">
                              <TranslatedText text="Amount (Frw)" />
                            </Label>
                            <Input
                              id="lifeInsuranceAmount"
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.lifeInsuranceAmount}
                              onChange={(e) => setFormData({ ...formData, lifeInsuranceAmount: e.target.value })}
                              placeholder={tSync("Enter amount")}
                              className="h-11 w-full border border-slate-300"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <PiggyBank className="w-4 h-4" />
                      <TranslatedText text="Retirement Benefits" />
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="retirement401k"
                          checked={formData.retirement401k}
                          onChange={(e) => setFormData({ ...formData, retirement401k: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <Label htmlFor="retirement401k" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                          <PiggyBank className="w-4 h-4 text-yellow-500" />
                          <TranslatedText text="401(k) Retirement Plan" />
                        </Label>
                      </div>
                      {formData.retirement401k && (
                        <div className="ml-7 space-y-2">
                          <Label htmlFor="retirement401kRate" className="text-sm font-medium text-slate-600">
                            <TranslatedText text="Contribution Rate (%)" />
                          </Label>
                          <Input
                            id="retirement401kRate"
                            type="number"
                            step="0.1"
                            value={formData.retirement401kRate}
                            onChange={(e) => setFormData({ ...formData, retirement401kRate: e.target.value })}
                            className="h-10 border border-slate-300"
                            placeholder={tSync("5.0")}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}
          </form>
        </div>
        
        <DialogFooter className="pt-6 border-t border-slate-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 w-full">
            <div className="flex gap-3">
              {currentStep > 1 && (
                <Button 
                  variant="outline" 
                  onClick={handlePrevious}
                  className="h-12 text-base px-6"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <TranslatedText text="Previous" />
                </Button>
              )}
            </div>
            
            <div>
              {currentStep < totalSteps ? (
                <Button 
                  type="button"
                  onClick={handleNext}
                  className="h-12 text-base px-6"
                >
                  <TranslatedText text="Next" />
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="h-12 text-base px-6 disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <MoonLoader size="sm" color="teal" />
                      <span className="ml-2">{employee ? <TranslatedText text="Updating..." /> : <TranslatedText text="Adding..." />}</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {employee ? <TranslatedText text="Update Employee" /> : <TranslatedText text="Add Employee" />}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Employee Details Modal Component
export function EmployeeDetailsModal({ 
  isOpen, 
  onClose, 
  employee 
}: {
  isOpen: boolean
  onClose: () => void
  employee?: any
}) {
  if (!employee) return null

  const formatCurrency = (amount: number | null | undefined, currency: string = 'RWF') => {
    if (!amount) return `${currency} 0.00`
    // For RWF, use custom formatting (no decimal places typically)
    if (currency === 'RWF' || currency === 'Frw') {
      return `${currency} ${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    }
    // For other currencies, use standard formatting
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'Frw' ? 'RWF' : currency
    }).format(amount)
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not specified'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getPayDisplay = () => {
    const currency = employee.salaryCurrency || 'RWF'
    if (employee.payRateType === 'salary') {
      return `${formatCurrency(employee.basicSalary, currency)}/month`
    } else if (employee.payRateType === 'hourly') {
      return `${formatCurrency(employee.payRate, currency)}/hour`
    } else if (employee.payRateType === 'commission') {
      return `Commission: ${employee.payRate}%`
    } else {
      return `${formatCurrency(employee.payRate, currency)}/${employee.payRateType}`
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{employee.firstName} {employee.lastName}</h2>
              <p className="text-muted-foreground">#{employee.employeeNumber}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">First Name</Label>
                  <p className="text-sm font-medium">{employee.firstName}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Last Name</Label>
                  <p className="text-sm font-medium">{employee.lastName}</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                <p className="text-sm font-medium">{employee.email}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Phone</Label>
                <p className="text-sm font-medium">{employee.phone || 'Not provided'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Date of Birth</Label>
                <p className="text-sm font-medium">{formatDate(employee.dateOfBirth)}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Social Security Number</Label>
                <p className="text-sm font-medium">{employee.socialSecurityNumber || 'Not provided'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Address Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Address</Label>
                <p className="text-sm font-medium">{employee.address || 'Not provided'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">City</Label>
                  <p className="text-sm font-medium">{employee.city || 'Not provided'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">State</Label>
                  <p className="text-sm font-medium">{employee.state || 'Not provided'}</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">ZIP Code</Label>
                <p className="text-sm font-medium">{employee.zipCode || 'Not provided'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Employment Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Employment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Hire Date</Label>
                <p className="text-sm font-medium">{formatDate(employee.hireDate)}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Department</Label>
                <p className="text-sm font-medium">{employee.department || 'Not assigned'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Position</Label>
                <p className="text-sm font-medium">{employee.position || 'Not specified'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Employment Type</Label>
                <p className="text-sm font-medium capitalize">{employee.employmentType?.replace('_', ' ') || 'Not specified'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Employment Status</Label>
                <p className="text-sm font-medium capitalize">{employee.employmentStatus?.replace('_', ' ') || 'Active'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Compensation Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Compensation Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Pay Rate Type</Label>
                <p className="text-sm font-medium capitalize">{employee.payRateType}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Compensation</Label>
                <p className="text-lg font-bold text-green-600">{getPayDisplay()}</p>
              </div>
              {employee.payRateType === 'salary' && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Salary Currency</Label>
                    <p className="text-sm font-medium">{employee.salaryCurrency || 'RWF'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Salary Effective Date</Label>
                    <p className="text-sm font-medium">{formatDate(employee.salaryEffectiveDate)}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Pay Frequency</Label>
                    <p className="text-sm font-medium capitalize">{employee.payFrequency}</p>
                  </div>
                </>
              )}
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Overtime Rate</Label>
                <p className="text-sm font-medium">{employee.overtimeRate ? `${employee.overtimeRate}x` : 'Not set'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Overtime Threshold</Label>
                <p className="text-sm font-medium">{employee.overtimeThreshold ? `${employee.overtimeThreshold} hours` : 'Not set'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Tax Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Tax Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Tax Filing Status</Label>
                <p className="text-sm font-medium capitalize">{employee.taxFilingStatus?.replace('_', ' ') || 'Single'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Federal Exemptions</Label>
                <p className="text-sm font-medium">{employee.federalExemptions || 0}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">State Exemptions</Label>
                <p className="text-sm font-medium">{employee.stateExemptions || 0}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Additional Withholding</Label>
                <p className="text-sm font-medium">{formatCurrency(employee.additionalWithholding, employee.salaryCurrency || 'RWF')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Benefits Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Benefits Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${employee.healthInsurance ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">Health Insurance</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${employee.dentalInsurance ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">Dental Insurance</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${employee.visionInsurance ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">Vision Insurance</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${employee.lifeInsurance ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">Life Insurance</span>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">401k Retirement</Label>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${employee.retirement401k ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm">
                    {employee.retirement401k ? `${employee.retirement401kRate || 0}%` : 'Not enrolled'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Payroll Period Modal Component
interface PayrollPeriodModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => void
}

export function PayrollPeriodModal({ isOpen, onClose, onSubmit }: PayrollPeriodModalProps) {
  const { tSync } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    payDate: ''
  })

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        startDate: '',
        endDate: '',
        payDate: ''
      })
      setErrors({})
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    // Validation
    const newErrors: Record<string, string> = {}
    
    if (!formData.startDate) {
      newErrors.startDate = tSync('Start date is required')
    }
    if (!formData.endDate) {
      newErrors.endDate = tSync('End date is required')
    }
    if (!formData.payDate) {
      newErrors.payDate = tSync('Pay date is required')
    }

    // Validate date logic
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate)
      const endDate = new Date(formData.endDate)
      if (endDate < startDate) {
        newErrors.endDate = tSync('End date must be after start date')
      }
    }

    if (formData.payDate && formData.endDate) {
      const endDate = new Date(formData.endDate)
      const payDate = new Date(formData.payDate)
      if (payDate < endDate) {
        newErrors.payDate = tSync('Pay date must be on or after end date')
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)
    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <CalendarIcon className="w-6 h-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-2xl font-semibold text-slate-900">
                <TranslatedText text="Add Payroll Period" />
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-1">
                <TranslatedText text="Create a new payroll period with start, end, and pay dates" />
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="startDate" className="text-sm font-medium">
              <TranslatedText text="Start Date" /> <span className="text-red-500">*</span>
            </Label>
            <Input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className={`h-11 w-full border ${errors.startDate ? 'border-red-500' : 'border-slate-300'}`}
              required
            />
            {errors.startDate && (
              <p className="text-sm text-red-500 mt-1">{errors.startDate}</p>
            )}
          </div>

          <div>
            <Label htmlFor="endDate" className="text-sm font-medium">
              <TranslatedText text="End Date" /> <span className="text-red-500">*</span>
            </Label>
            <Input
              id="endDate"
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className={`h-11 w-full border ${errors.endDate ? 'border-red-500' : 'border-slate-300'}`}
              required
            />
            {errors.endDate && (
              <p className="text-sm text-red-500 mt-1">{errors.endDate}</p>
            )}
          </div>

          <div>
            <Label htmlFor="payDate" className="text-sm font-medium">
              <TranslatedText text="Pay Date" /> <span className="text-red-500">*</span>
            </Label>
            <Input
              id="payDate"
              type="date"
              value={formData.payDate}
              onChange={(e) => setFormData({ ...formData, payDate: e.target.value })}
              className={`h-11 w-full border ${errors.payDate ? 'border-red-500' : 'border-slate-300'}`}
              required
            />
            {errors.payDate && (
              <p className="text-sm text-red-500 mt-1">{errors.payDate}</p>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button 
              variant="outline" 
              type="button"
              onClick={onClose}
              className="h-12 text-base px-6"
            >
              <TranslatedText text="Cancel" />
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="h-12 text-base px-6 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <MoonLoader size="sm" color="teal" />
                  <span className="ml-2"><TranslatedText text="Creating..." /></span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  <TranslatedText text="Create Period" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
